import { Example } from '@/types/Example';
import { Repeat } from 'lucide-react';
import React, { useEffect } from 'react';
import QuizzQuestion, { AnswerLogEntry } from './QuizzQuestion';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader } from './ui/card';
import { Progress } from './ui/progress';
import { createBrowserClientSupa } from '@/utils/supabase/createBrowserClient';
import AnswerIndicator from './AnswerIndicator';
import { useGetSupabaseSession } from '@/hooks/useGetSupabaseSession';

const QUIZZ_LENGTH = 10;

interface TopQuizzAreaProps {
  answeredExamples: AnswerLogEntry[];
  unlearnedExamples: Example[];
  onAnswer: (
    isCorrect: boolean,
    exampleId: string,
    answerGiven: string,
  ) => void;
  quizType: string;
  onStartNewQuizz: () => void;
}

const TopQuizzArea = (props: TopQuizzAreaProps) => {
  const {
    answeredExamples,
    unlearnedExamples,
    onAnswer,
    quizType,
    onStartNewQuizz,
  } = props;
  const isCommandQuiz = quizType === 'command';
  const session = useGetSupabaseSession();
  const userId = session?.user.id;
  const supabase = createBrowserClientSupa();
  const [answerIndicatorState, setAnswerIndicatorState] = React.useState<
    boolean | undefined
  >(undefined);

  const numCorrectAnswers = answeredExamples.filter(
    (example) => example.correct,
  ).length;
  const numTotalAnswers = answeredExamples.length;

  useEffect(() => {
    const lastAnswerState = answeredExamples.length
      ? answeredExamples[answeredExamples.length - 1].correct
      : undefined;
    setAnswerIndicatorState(lastAnswerState);
  }, [answeredExamples]);

  const handleAnimateEnd = () => {
    setAnswerIndicatorState(undefined);
  };

  useEffect(() => {
    const saveCompletedQuizz = async () => {
      await supabase.from('completed_quizzes').insert([
        {
          num_questions: QUIZZ_LENGTH,
          num_correct: numCorrectAnswers,
          quiz_type: quizType,
          user_id: userId,
        },
      ]);
    };
    if (answeredExamples.length >= QUIZZ_LENGTH && userId) {
      saveCompletedQuizz();
    }
  }, [answeredExamples.length, numCorrectAnswers, quizType, supabase, userId]);

  const content =
    answeredExamples.length >= QUIZZ_LENGTH ? (
      <CardContent className="mx-auto flex h-full max-w-3xl flex-col items-center justify-center">
        <p className="mb-2 font-bold text-green-600">
          🎉 You finished the quiz!
        </p>
        <p className="mb-4 text-center">
          You got {numCorrectAnswers} out of {QUIZZ_LENGTH} questions correct.
        </p>
        <Button onClick={onStartNewQuizz}>
          <Repeat size={12} className="mr-2" />
          <span>Take another one</span>
        </Button>
      </CardContent>
    ) : (
      <>
        <CardHeader className="relative mx-auto border-b sm:mx-0">
          <p className="text-xs text-gray-500 sm:text-sm">
            Answered {numTotalAnswers} out of {QUIZZ_LENGTH} questions
          </p>
          <div className="flex min-h-[20px] w-full justify-between">
            <Progress
              className="my-progress max-w-[200px] bg-gray-100"
              value={Math.ceil((numTotalAnswers * 100) / QUIZZ_LENGTH)}
            />
            <div className="absolute top-4 right-4">
              <AnswerIndicator
                answer={answerIndicatorState}
                onAnimateEnd={handleAnimateEnd}
              />
            </div>
          </div>
        </CardHeader>
        <CardContent className="flex h-full w-full max-w-3xl flex-1 flex-col items-center pt-4">
          <QuizzQuestion
            unlearnedExamples={unlearnedExamples}
            answeredExamples={answeredExamples}
            onAnswer={onAnswer}
            isCommandQuizz={isCommandQuiz}
          />
        </CardContent>
      </>
    );
  return (
    <Card className="flex min-h-[300px] w-full flex-col items-stretch justify-center">
      {content}
    </Card>
  );
};

export default TopQuizzArea;
